/**
 * Action color-coding based on canonical verb.noun naming
 */
export function getActionBadgeTone(action: string): { bg: string; text: string; dot: string } {
  const lower = action.toLowerCase();
  if (
    lower.includes('create') ||
    lower.includes('approved') ||
    lower.includes('verified') ||
    lower.includes('restored') ||
    lower.includes('enabled') ||
    lower.includes('added')
  ) {
    return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' };
  }
  if (
    lower.includes('delete') ||
    lower.includes('suspend') ||
    lower.includes('reject') ||
    lower.includes('failed') ||
    lower.includes('cancel') ||
    lower.includes('revoked')
  ) {
    return { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-800', dot: 'bg-rose-500' };
  }
  if (lower.includes('update') || lower.includes('changed') || lower.includes('assign')) {
    return { bg: 'bg-sky-50 border-sky-200', text: 'text-sky-800', dot: 'bg-sky-500' };
  }
  if (lower.includes('paid') || lower.includes('payout')) {
    return { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-800', dot: 'bg-purple-500' };
  }
  return { bg: 'bg-gray-100 border-gray-200', text: 'text-gray-800', dot: 'bg-gray-500' };
}

/**
 * Relative time formatter (e.g., '2m ago', '3d ago')
 */
export function formatRelativeTime(dateString: string): string {
  const d = new Date(dateString);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Full date and time formatter
 */
export function formatFullDateTime(dateString: string): string {
  const d = new Date(dateString);
  return d.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

/**
 * Dynamically parses standard HTTP User-Agent strings into a clean, human-readable
 * format like "Chrome 130 • Windows" instead of raw "Mozilla/5.0".
 */
export function formatUserAgent(ua?: string | null): string {
  if (!ua) return '';
  const s = ua.trim();
  if (!s) return '';

  if (s.toLowerCase() === 'node' || s.startsWith('node/')) return 'Node.js';
  if (s.startsWith('Postman')) return 'Postman';
  if (s.startsWith('curl')) return 'cURL';

  // Detect Operating System
  let os = '';
  if (/windows nt 10/i.test(s)) os = 'Windows 10/11';
  else if (/windows nt 6\.3/i.test(s)) os = 'Windows 8.1';
  else if (/windows nt 6\.2/i.test(s)) os = 'Windows 8';
  else if (/windows nt 6\.1/i.test(s)) os = 'Windows 7';
  else if (/windows/i.test(s)) os = 'Windows';
  else if (/macintosh|mac os x/i.test(s)) os = 'macOS';
  else if (/android/i.test(s)) os = 'Android';
  else if (/iphone|ipad|ipod/i.test(s)) os = 'iOS';
  else if (/linux/i.test(s)) os = 'Linux';

  // Detect Browser (Edge before Chrome, Chrome before Safari)
  let browser = '';
  if (/edg\/(\d+)/i.test(s)) {
    const m = s.match(/edg\/(\d+)/i);
    browser = m ? `Edge ${m[1]}` : 'Edge';
  } else if (/opr\/(\d+)|opera/i.test(s)) {
    const m = s.match(/opr\/(\d+)/i);
    browser = m ? `Opera ${m[1]}` : 'Opera';
  } else if (/chrome\/(\d+)/i.test(s)) {
    const m = s.match(/chrome\/(\d+)/i);
    browser = m ? `Chrome ${m[1]}` : 'Chrome';
  } else if (/firefox\/(\d+)/i.test(s)) {
    const m = s.match(/firefox\/(\d+)/i);
    browser = m ? `Firefox ${m[1]}` : 'Firefox';
  } else if (/safari/i.test(s) && !/chrome/i.test(s)) {
    browser = 'Safari';
  }

  if (browser && os) return `${browser} • ${os}`;
  if (browser) return browser;
  if (os) return os;

  return s.split(' ')[0];
}
